import { get } from 'lodash';
import styled from '@emotion/styled';
import { withRouter } from 'react-router';
import React, { useMemo, useContext } from 'react';

import Mobile from './Previews/Mobile';
import { assetUrl } from '~/helpers';
import Desktop from './Previews/Desktop';
import EditorContext, { EditorContextProps } from '../Editor/Context';
import { getProfileName, getProfileFirstName } from '~/BonjorosList/util';
import { List, Map } from 'immutable';
import { MessageTemplateType } from '~/types/models';
import { EmbedService } from '../../Types';
import useCurrentUser from '~/react-hooks/use-current-user';
import { useFeatures } from '~/react-hooks/use-features';

const Container = styled.div`
  text-align: center;
`;

export interface PreviewProps {
  ctaUrl?: string;
  ctaText: string;
  showCta: boolean;
  color: string;
  secondaryColor: string;
  userImage: string;
  userName: string;
  userFirstName: string;
  userLastName: string;
  userShortName: string;
  playerImage?: string;
  backgroundUrl?: string;
  logoUrl: string;
  message?: string;
  highlight?: string;
  className?: string;
  showBonjoroBadge?: boolean;
  showSocialSharing: boolean;
  embeds?: List<Map<string, string>>;
  embedServices?: List<EmbedService>;
  templateType?: MessageTemplateType;
  canAccessNextGenTemplate: boolean;
  hidePreviewTag?: boolean;
}

function LandingPagePreview() {
  const editorContext: EditorContextProps = useContext(EditorContext);
  const { preview } = editorContext;
  const user = useCurrentUser();
  const { canAccessNextGenTemplate } = useFeatures();

  const backgroundUrl = useMemo(() => {
    const backgroundId = editorContext.get<string>([
      'landing_page',
      'background',
      'id',
    ]);

    if (backgroundId) {
      return editorContext.get<string>(['landing_page', 'background', 'url']);
    }
    return '';
  }, [editorContext]);

  const contextLogoUrl = editorContext.get<string>([
    'landing_page',
    'logo',
    'url',
  ]);
  const logoUrl =
    (contextLogoUrl as string) ||
    assetUrl('/assets/images/logos/logo_full.png');

  const previewProps: PreviewProps = {
    ctaUrl: editorContext.get<string>(
      ['landing_page', 'cta', 'uri'],
      get(window, 'Bonjoro.messageTemplate.defaultCtaUrl')
    ),
    ctaText: editorContext.get<string>(
      ['landing_page', 'cta', 'text'],
      get(window, 'Bonjoro.messageTemplate.defaultCtaText')
    ) as string,
    showCta: editorContext.get<boolean>(
      ['landing_page', 'cta', 'is_displayed'],
      get(window, 'Bonjoro.messageTemplate.defaultIsCtaDisplayed')
    ) as boolean,
    color: editorContext.get<string>(
      ['theme', 'primary_color'],
      '#fc5a00'
    ) as string,
    secondaryColor: editorContext.get<string>(
      ['theme', 'secondary_color'],
      '#ffffff'
    ) as string,
    userImage:
      user.image ||
      assetUrl('/assets/images/message-template/default-preview.jpg'),
    userName: getProfileName(user),
    userShortName: getProfileFirstName(user),
    userFirstName: user.first_name ?? '',
    userLastName: user.last_name ?? '',
    playerImage: editorContext?.metadata?.playerImage,
    backgroundUrl,
    logoUrl,
    message: editorContext?.compiledTemplates?.message as string,
    showBonjoroBadge: !get(user, 'features.shouldHideBonjoroBranding', true),
    showSocialSharing: editorContext.get('is_greet_shareable') as boolean,
    embedServices: editorContext.embedServices,
    embeds: editorContext.get('embeds') as List<Map<string, string>>,
    templateType: editorContext.get('email_template.template_type'),
    canAccessNextGenTemplate,
  };

  const isMobile = useMemo(() => {
    return preview === 'mobile';
  }, [preview]);

  return (
    <Container>
      {isMobile ? <Mobile {...previewProps} /> : <Desktop {...previewProps} />}
    </Container>
  );
}

export default withRouter(LandingPagePreview);
